import React, { useCallback, useMemo, useReducer, useState } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogContent,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    Box,
    FormLabel,
    FormGroup,
    FormControlLabel,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';
import ProvinceSelect from '../../../common/provinceSelect';
import CitySelect from '../../../common/citySelect';
import AppDialogTitle from '../../../ui/appDialogTitle';
import { isLocalDev } from '../../../../utils/environment';
import { ValidDataService } from '../../../../service/validDataService';
import validLookup from '../../../../utils/validLookup';
import SectionTitle from '../../../ui/sectionTitle';
import useAbortController from '../../../../hooks/useAbortController';
import SafeDialogContent from 'components/ui/SafeDialogContent';

export const initialValuesSupplierForm = {
    supplierName: '',
    suppliesFrames: false,
    suppliesLenses: false,
    suppliesContacts: false,
    suppliesAccessories: false,
    phone1: '',
    phone2: '',
    fax: '',
    email: '',
    addrNo: '',
    addrUnit: '',
    addrStreet: '',
    addrCity: '',
    addrProvince: '',
    addrPostal: '',
    website: '',
    status: validLookup.validStatus.active,
    notes: '',
};

export default function ValidSupplierInputForm({ open, onClose, onRecordAdded }) {

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            const response = await ValidDataService.addValidSupplier(values)
            const { code, data, message } = response

            setSubmitting(false)

            if (code === 0) {
                toast.success(message);
                resetForm();
                onRecordAdded(data)
            } else {
                toast.success(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            setSubmitting(false)
            toast.error('Failed to add record');
        }
    };

    return (
        <Dialog open={open} maxWidth={'lg'} fullWidth>
            <AppDialogTitle title={"Add Supplier"} onClose={onClose} />
            <SafeDialogContent>
                <Formik
                    initialValues={initialValuesSupplierForm}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, isSubmitting, handleChange }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4} mt={2}>
                                    <Field
                                        as={TextField}
                                        name="supplierName"
                                        label="Supplier Name"
                                        required
                                        fullWidth
                                        size="small"
                                    />
                                    <ErrorMessage name="supplierName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={4} mt={2}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12} sm={4} mt={2}>
                                    
                                        <FormControl component="fieldset" variant="standard">
                                            <FormLabel component="legend">Supplier Type</FormLabel>
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={<Field
                                                        as={Checkbox}
                                                        name="suppliesFrames"
                                                        label="Frames"
                                                        value={values.suppliesFrames === true}
                                                    />}
                                                    label="Frames"
                                                />
                                                <FormControlLabel
                                                    control={<Field
                                                        as={Checkbox}
                                                        name="suppliesLenses"
                                                        label="Lenses"
                                                        value={values.suppliesLenses === true}
                                                    />}
                                                    label="Lenses"
                                                />
                                            </FormGroup>
                                        </FormControl>
                                        <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={<Field
                                                        as={Checkbox}
                                                        name="suppliesContacts"
                                                        label="Frames"
                                                        value={values.suppliesContacts === true}
                                                    />}
                                                    label="Contacts"
                                                />
                                                <FormControlLabel
                                                    control={<Field
                                                        as={Checkbox}
                                                        name="suppliesAccessories"
                                                        label="Lenses"
                                                        value={values.suppliesAccessories === true}
                                                    />}
                                                    label="Accessories"
                                                />
                                            </FormGroup>
                                        </FormControl>                                        
                                    {/* <FormControl fullWidth>
                                        <InputLabel>Supplier Type</InputLabel>
                                         <Field
                                            as={Select}
                                            name="supplierType"
                                            label="Supplier Type"
                                            multiple
                                            size="small"
                                            renderValue={(selected) => ["one","two"].join(', ')}
                                            onChange={(event) => setFieldValue(event.target.name, event.target.value)}
                                        >
                                            {supplierTypes.map((type) => (
                                                <MenuItem key={type.value} value={type.label}>
                                                    <Checkbox checked={values[type.value] || false} />
                                                    <ListItemText primary={type.label} />
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </FormControl> */}
                                    <ErrorMessage name="supplierType">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>

                                {/* Address and Contact */}
                                <Grid item xs={12}>
                                    <SectionTitle title="Address and Contact" />
                                </Grid>
                                <Grid container spacing={2} p={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="addrNo"
                                                label="Number"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="addrNo">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="addrStreet"
                                                label="Street"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="addrStreet">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="addrUnit"
                                                label="Unit"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="addrUnit">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                name="addrCity"
                                                component={CitySelect}
                                                countryId={1} // Pass the countryId as a prop
                                                label="City"
                                                size="small"
                                            />
                                            <ErrorMessage name="addrCity">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                name="addrProvince"
                                                component={ProvinceSelect}
                                                countryId={1} // Pass the countryId as a prop
                                                label="Province"
                                                size="small"
                                            />
                                            <ErrorMessage name="addrProvince">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="addrPostal"
                                                label="Postal code"
                                                size="small"
                                                fullWidth
                                            />
                                            <ErrorMessage name="addrPostal">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="phone1"
                                                label="Phone Number 1"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="phone1">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="phone2"
                                                label="Phone Number 2"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="phone2">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="email"
                                                label="Email"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="email">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="fax"
                                                label="Fax"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="fax">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="website"
                                                label="Website"
                                                fullWidth
                                                size="small"
                                            />
                                            <ErrorMessage name="website">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <SectionTitle title="Additional Info" />
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={4}
                                        fullWidth
                                    />
                                    <ErrorMessage name="notes">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        Create
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>

            </SafeDialogContent>
        </Dialog>
    );
};